/**
 * Persistent AI workspaces — the registry that says which connections keep
 * their workspace folder, and the rules for naming those folders (Task T16).
 *
 * Shared by the renderer (which owns the registry, in `aiTerminalSettings`)
 * and the Electron main process (which turns a `folderName` into a real
 * directory). Extracted from `aiTerminalSettings.ts` so both the settings
 * module and this concern stay near the repo's file-size guidance.
 *
 * ## Folder identity comes from the connection ID (fix round 1, C-1)
 *
 * The first cut derived the folder from the connection's *display name*
 * (`alias || server`). Aliases are optional and not unique
 * (`connectionFormSchema.ts`), so two unaliased connections to one server —
 * one server, two databases, the most ordinary setup there is — resolved to a
 * single folder. Two consequences, neither needing a disk error or a
 * hand-edited file: the two connections shared one workspace (mixing notes and
 * `.schema/` DDL from two databases), and "Stop keeping" on either one cleared
 * the shared marker while the other's registry entry and toggle still said
 * kept — so the next app start wiped the other connection's notes.
 *
 * `connectionId` is the only stable per-connection identity, so it is what the
 * folder name is derived from. A sanitized display-name prefix is kept purely
 * so the folder is recognisable on disk; the digest is what makes it unique.
 */

import { safeServerText } from './safeConnectionDisplay';

/** Hard cap on a workspace folder name. */
const MAX_WORKSPACE_FOLDER_NAME_LENGTH = 64;

/** Suffix marking a folder as a connection's kept workspace. */
const PERSISTENT_FOLDER_SUFFIX = '_persistent';

/** Length of the connection-id digest embedded in a kept folder's name. */
const ID_DIGEST_LENGTH = 8;

/**
 * One opted-in persistent AI workspace, keyed by connection id in
 * `AiTerminalSettings.persistentWorkspaces`.
 */
export type PersistentWorkspaceEntry = {
  /**
   * Folder name — NOT a path — directly under the AI workspace base folder.
   * Always *derived* via `persistentWorkspaceFolderName()`; never free-typed.
   * Re-sanitized on every read anyway, because a hand-edited settings file is
   * a real input and this value is joined onto a filesystem path in main.
   */
  folderName: string;
};

/** connectionId → the folder its AI workspace is kept in. */
export type PersistentWorkspaceRegistry = Record<string, PersistentWorkspaceEntry>;

/**
 * Update shape for the registry. `null` means "de-register this connection"
 * — the map has to express removal, which a plain `Partial<>` cannot.
 * De-registering never deletes the folder.
 */
export type PersistentWorkspaceRegistryUpdate = Record<string, PersistentWorkspaceEntry | null>;

/**
 * Reduce an arbitrary display name to a single, safe path segment.
 *
 * The sole sanitizer for every workspace folder name in the app — the AI
 * workspace manager imports it rather than keeping its own copy, so a name
 * generated in the renderer and a name re-read from a (possibly hand-edited)
 * settings file go through exactly the same rules.
 *
 * Output is restricted to `[A-Za-z0-9_-]`, which by construction removes the
 * whole path-traversal surface: no `/`, no `\`, no `.`, so no `..` segment and
 * no absolute path can survive. Idempotent — sanitizing an already-sanitized
 * name returns it unchanged.
 */
export function toWorkspaceFolderSafeName(value: string): string {
  const trimmed = value.trim();
  const collapsed = trimmed.replace(/\s+/g, '-');
  const cleaned = collapsed.replace(/[^a-zA-Z0-9_-]/g, '-');
  const normalized = cleaned.replace(/-+/g, '-').replace(/^[-_]+|[-_]+$/g, '');
  const limited = normalized.slice(0, MAX_WORKSPACE_FOLDER_NAME_LENGTH);
  return limited || 'connection';
}

/**
 * Short, stable digest of a connection id — FNV-1a (32-bit) in base36.
 *
 * Hand-rolled on purpose: this runs in the renderer *and* in main, so it
 * cannot use node `crypto`, and it must produce the same string in both. Pure
 * arithmetic over char codes, no platform or locale input.
 */
export function connectionIdDigest(connectionId: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < connectionId.length; i += 1) {
    hash ^= connectionId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(ID_DIGEST_LENGTH, '0').slice(-ID_DIGEST_LENGTH);
}

/**
 * The folder a connection's kept AI workspace lives in:
 * `<display-name>-<id-digest>_persistent`.
 *
 * Derived, never user-typed. Identity is the **digest**, so two connections
 * that display identically still get different folders (C-1); the name part is
 * only there to make the folder recognisable in a file browser. The name part
 * is trimmed so the digest and suffix always fit inside the length cap, which
 * keeps the result a **fixed point** of `toWorkspaceFolderSafeName` — so
 * re-sanitizing on read can never silently rename a folder.
 */
export function persistentWorkspaceFolderName(connectionName: string, connectionId: string): string {
  const suffix = `-${connectionIdDigest(connectionId)}${PERSISTENT_FOLDER_SUFFIX}`;
  // A folder name on disk never carries a secret from a name built out of server text (security slice 2026-09-28).
  const namePart = toWorkspaceFolderSafeName(safeServerText(connectionName) || connectionName).slice(
    0,
    MAX_WORKSPACE_FOLDER_NAME_LENGTH - suffix.length
  );
  return toWorkspaceFolderSafeName(`${namePart}${suffix}`);
}

/**
 * Reduce a persisted `persistentWorkspaces` blob to a registry that is safe to
 * hand to `fs` code in the main process.
 *
 * Rejection is per-entry and silent by design — one malformed entry must not
 * take the rest of the registry (or the whole settings blob) down with it.
 * Dropping an entry is non-destructive: it only means the connection stops
 * *reusing* that folder. The folder itself is protected on disk by the
 * `.jam-persistent` marker `workspacePersistence` writes, so a dropped entry
 * never turns into a deletion.
 *
 * A `folderName` claimed by more than one connectionId is dropped for every
 * connection after the first (see `claimFolderNames`) — the same C-1 rule the
 * merge applies, enforced again here because a persisted blob can also be
 * hand-edited into a collision.
 */
export function normalizePersistentWorkspaces(input: unknown): PersistentWorkspaceRegistry {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const candidate: PersistentWorkspaceRegistry = {};
  for (const [connectionId, raw] of Object.entries(input as Record<string, unknown>)) {
    // Never let a persisted key reach `result[key] = …` as a prototype write.
    if (connectionId === '__proto__' || connectionId === 'constructor' || connectionId === 'prototype') continue;
    if (!connectionId.trim()) continue;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;

    const folderName = (raw as Record<string, unknown>).folderName;
    // An empty/whitespace-only or non-string folderName names no folder at all;
    // coercing it would invent one (`toWorkspaceFolderSafeName('')` falls back
    // to `connection`), so the entry is dropped instead.
    if (typeof folderName !== 'string' || !folderName.trim()) continue;

    candidate[connectionId] = { folderName: toWorkspaceFolderSafeName(folderName) };
  }
  return claimFolderNames(candidate);
}

/**
 * Keep at most ONE connection per folder name.
 *
 * Two registry entries pointing at one folder is the shape that made C-1
 * destructive: de-registering either one clears the shared marker while the
 * other still believes it is kept, and the next startup cleanup deletes the
 * folder out from under it.
 *
 * The rule is **first key wins in the registry's own insertion order** — which
 * is *not* the same as "the older claim wins", and the comment used to say the
 * latter (N-3). `mergePersistentWorkspaces` spreads `{...current}` first, and
 * re-assigning an existing key does not move it, so for
 * `merge({a:{F}, b:{G}}, {a:{G}})` the earlier key `a` takes `G` and the
 * incumbent `b` is dropped. Reaching that needs two connections with identical
 * display names *and* an FNV-1a collision on their ids, so it is unreachable
 * through the UI; and it is not destructive when it happens, because `b`'s
 * folder keeps its marker and merely stops being reused. Left as-is
 * deliberately: seeding `claimed` from `current` would make seniority
 * structural but would also make a legitimate re-point of an existing entry
 * fail.
 *
 * Claims are compared on the **sanitized** name (N-4), so the two callers
 * agree: `normalizePersistentWorkspaces` sanitizes its entries before claiming,
 * `mergePersistentWorkspaces` hands over whatever the caller passed. Only the
 * comparison key is sanitized — the entry is stored verbatim, and the persisted
 * blob is re-normalized on rehydrate.
 */
function claimFolderNames(registry: PersistentWorkspaceRegistry): PersistentWorkspaceRegistry {
  const claimed = new Set<string>();
  const result: PersistentWorkspaceRegistry = {};
  for (const [connectionId, entry] of Object.entries(registry)) {
    // Defensive: `merge` is typed but its input can come from a hand-edited
    // blob, and a throw here would break every settings write.
    const claimKey =
      typeof entry?.folderName === 'string' ? toWorkspaceFolderSafeName(entry.folderName) : '';
    if (claimed.has(claimKey)) continue;
    claimed.add(claimKey);
    result[connectionId] = entry;
  }
  return result;
}

/**
 * Apply an entry-level registry update on top of the current registry.
 *
 * Load-bearing: without it, `setAiTerminalSettings({ persistentWorkspaces: { b: … } })`
 * would *replace* the whole map through the generic `...settings` spread and
 * silently drop every other connection's entry. `null` removes one entry.
 *
 * Collisions are **rejected, not merged**: an incoming entry whose folderName
 * another connection already claims is dropped, leaving the existing owner
 * untouched. The caller can detect this by reading the registry back — the
 * toggle does exactly that and refuses to show ON for an entry that did not
 * land.
 */
export function mergePersistentWorkspaces(
  current: PersistentWorkspaceRegistry,
  update: PersistentWorkspaceRegistryUpdate | undefined
): PersistentWorkspaceRegistry {
  if (!update) return current;

  const next: PersistentWorkspaceRegistry = { ...current };
  for (const [connectionId, entry] of Object.entries(update)) {
    if (entry === null || entry === undefined) {
      delete next[connectionId];
      continue;
    }
    // Removals are applied above first, so releasing a folder and re-claiming
    // it for another connection in one call works.
    next[connectionId] = entry;
  }
  return claimFolderNames(next);
}
